import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import PositionRangeStatus from 'components/Badge/PositionRangeStatus'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import CurrencyLogo from 'components/Logo/CurrencyLogo'
import {
  CellPrimary,
  CellSecondary,
  ExpandablePositionTableCard,
  formatPositionUSD,
  PoolIdentity,
  PositionCell,
  PositionMetrics,
  PositionTableSummaryButton,
  RangeValues,
  TokenDistribution,
  VersionBadge,
} from 'components/PositionList/PositionTable'
import { Dots } from 'components/swap/styleds'
import { UNSUPPORTED_V2POOL_CHAIN_IDS } from 'constants/chains'
import { BIG_INT_ZERO } from 'constants/misc'
import { WRAPPED_NATIVE_CURRENCY } from 'constants/tokens'
import { Pair } from 'elephantswapv2-sdk'
import { CurrencyAmount, Token } from 'elephantswapv3-sdk-core'
import { useColor } from 'hooks/useColor'
import { useTotalSupply } from 'hooks/useTotalSupply'
import { useUSDPrice } from 'hooks/useUSDPrice'
import { useV2Pairs } from 'hooks/useV2Pairs'
import JSBI from 'jsbi'
import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronUp } from 'react-feather'
import { Link } from 'react-router-dom'
import { useTokenBalance, useTokenBalancesWithLoadingIndicator } from 'state/connection/hooks'
import { useStakingInfo } from 'state/stake/hooks'
import { toV2LiquidityToken, useTrackedTokenPairs } from 'state/user/hooks'
import styled from 'styled-components/macro'
import { currencyId } from 'utils/currencyId'
import { unwrappedToken } from 'utils/unwrappedToken'

export type V2LiquiditySummary = {
  estimatedValue?: number
  oneLocked: number
  positions: number
}

const EmptyState = styled.div`
  display: grid;
  order: 9998;
  min-height: 120px;
  padding: 24px;
  color: ${({ theme }) => theme.textTertiary};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 14px;
  text-align: center;
  place-items: center;
`

const V2Card = styled(ExpandablePositionTableCard)`
  overflow: hidden;
`

const V2Summary = styled(PositionTableSummaryButton)`
  position: relative;
`

const ExpandIndicator = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  display: grid;
  width: 22px;
  height: 22px;
  color: ${({ theme }) => theme.textSecondary};
  background: ${({ theme }) => theme.backgroundModule};
  border-radius: 50%;
  place-items: center;
`

const ExpandedDetails = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 18px;
  padding: 14px 20px 16px;
  background: ${({ theme }) => theme.backgroundSurface};
  border-top: 1px solid ${({ theme }) => theme.backgroundOutline};
`

const DepositedTokens = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 28px;
`

const DepositedToken = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 170px;
`

const TokenAmount = styled.div`
  min-width: 0;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 13px;
  font-weight: 600;
`

const V2Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ActionLink = styled(Link)`
  min-width: 92px;
  padding: 9px 14px;
  color: ${({ theme }) => theme.textSecondary};
  background: ${({ theme }) => theme.backgroundModule};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease, filter 120ms ease;

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.accentAction};
    background: ${({ theme }) => theme.accentActionSoft};
    border-color: ${({ theme }) => theme.accentAction};
    filter: brightness(1.1);
    outline: none;
  }
`

function V2PositionListItem({
  pair,
  stakedBalance,
  searchQuery,
  visible,
  order,
  onPositionMetricsChange,
}: {
  pair: Pair
  stakedBalance?: CurrencyAmount<Token>
  searchQuery: string
  visible: boolean
  order?: number
  onPositionMetricsChange: (positionKey: string, metrics?: PositionMetrics) => void
}) {
  const { account, chainId } = useWeb3React()
  const [expanded, setExpanded] = useState(false)
  const currency0 = unwrappedToken(pair.token0)
  const currency1 = unwrappedToken(pair.token1)
  const userDefaultPoolBalance = useTokenBalance(account ?? undefined, pair.liquidityToken)
  const totalPoolTokens = useTotalSupply(pair.liquidityToken)
  const userPoolBalance = stakedBalance ? userDefaultPoolBalance?.add(stakedBalance) : userDefaultPoolBalance
  const [token0Deposited, token1Deposited] =
    totalPoolTokens && userPoolBalance && JSBI.greaterThanOrEqual(totalPoolTokens.quotient, userPoolBalance.quotient)
      ? [
          pair.getLiquidityValue(pair.token0, totalPoolTokens, userPoolBalance, false),
          pair.getLiquidityValue(pair.token1, totalPoolTokens, userPoolBalance, false),
        ]
      : [undefined, undefined]
  const { data: token0USDValue } = useUSDPrice(token0Deposited)
  const { data: token1USDValue } = useUSDPrice(token1Deposited)
  const token0Color = useColor(pair.token0)
  const token1Color = useColor(pair.token1, '#9B5DE5')
  const positionKey = `v2:${pair.liquidityToken.address}`

  const estimatedValue = useMemo(() => {
    const value =
      token0USDValue !== undefined && token1USDValue !== undefined
        ? token0USDValue + token1USDValue
        : token0USDValue !== undefined
        ? token0USDValue * 2
        : token1USDValue !== undefined
        ? token1USDValue * 2
        : undefined
    return value !== undefined && Number.isFinite(value) ? value : undefined
  }, [token0USDValue, token1USDValue])

  const token0Percent = useMemo(() => {
    if (token0USDValue !== undefined && token1USDValue !== undefined && token0USDValue + token1USDValue > 0) {
      return (token0USDValue / (token0USDValue + token1USDValue)) * 100
    }
    if (token0Deposited?.equalTo(0)) return 0
    if (token1Deposited?.equalTo(0)) return 100
    return 50
  }, [token0Deposited, token0USDValue, token1Deposited, token1USDValue])

  const oneLocked = useMemo(() => {
    const wrappedOne = chainId ? WRAPPED_NATIVE_CURRENCY[chainId] : undefined
    if (!wrappedOne || !token0Deposited || !token1Deposited) return undefined
    const wrappedOneAmount = pair.token0.equals(wrappedOne)
      ? token0Deposited
      : pair.token1.equals(wrappedOne)
      ? token1Deposited
      : undefined
    if (!wrappedOneAmount) return 0
    const amount = Number(wrappedOneAmount.toSignificant(12))
    return Number.isFinite(amount) ? amount : undefined
  }, [chainId, pair.token0, pair.token1, token0Deposited, token1Deposited])

  useEffect(() => {
    onPositionMetricsChange(positionKey, { estimatedValue, oneLocked })
    return () => onPositionMetricsChange(positionKey, undefined)
  }, [estimatedValue, onPositionMetricsChange, oneLocked, positionKey])

  const normalizedSearch = searchQuery.trim().toLowerCase()
  const matchesSearch = [pair.token0.symbol, pair.token1.symbol, pair.token0.address, pair.token1.address]
    .filter(Boolean)
    .some((value) => value?.toLowerCase().includes(normalizedSearch))

  return (
    <V2Card $order={order} $hidden={!visible || Boolean(normalizedSearch && !matchesSearch)} $expanded={expanded}>
      <V2Summary
        type="button"
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${currency0.symbol}/${currency1.symbol} V2 position`}
        onClick={() => setExpanded((current) => !current)}
      >
        <PositionCell>
          <PoolIdentity>
            <DoubleCurrencyLogo currency0={currency0} currency1={currency1} size={20} margin />
            <div>
              <CellPrimary>
                {currency0.symbol} / {currency1.symbol}
              </CellPrimary>
              <CellSecondary>
                <VersionBadge>v2</VersionBadge>
                <span>0.3%</span>
              </CellSecondary>
            </div>
          </PoolIdentity>
        </PositionCell>
        <PositionCell>
          <RangeValues>
            <span>0</span>
            <span>→</span>
            <span>∞</span>
          </RangeValues>
          <CellSecondary>
            <Trans>Full Range</Trans>
          </CellSecondary>
          <CellSecondary>
            <PositionRangeStatus inRange />
          </CellSecondary>
        </PositionCell>
        <PositionCell>
          <TokenDistribution
            firstPercent={token0Percent}
            firstColor={token0Color}
            secondColor={token1Color}
            firstSymbol={currency0.symbol}
            secondSymbol={currency1.symbol}
          />
        </PositionCell>
        <PositionCell>
          <CellPrimary>{formatPositionUSD(estimatedValue)}</CellPrimary>
        </PositionCell>
        <PositionCell>
          <CellPrimary>—</CellPrimary>
        </PositionCell>
        <PositionCell>
          <CellPrimary>—</CellPrimary>
        </PositionCell>
        <ExpandIndicator>{expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</ExpandIndicator>
      </V2Summary>
      {expanded && (
        <ExpandedDetails>
          <DepositedTokens>
            <DepositedToken>
              <CurrencyLogo currency={currency0} size="24px" />
              <TokenAmount>
                {token0Deposited?.toSignificant(6) ?? '—'} {currency0.symbol}
              </TokenAmount>
            </DepositedToken>
            <DepositedToken>
              <CurrencyLogo currency={currency1} size="24px" />
              <TokenAmount>
                {token1Deposited?.toSignificant(6) ?? '—'} {currency1.symbol}
              </TokenAmount>
            </DepositedToken>
          </DepositedTokens>
          <V2Actions>
            <ActionLink to={`/add/v2/${currencyId(currency0)}/${currencyId(currency1)}`}>Add liquidity</ActionLink>
            <ActionLink to={`/remove/v2/${currencyId(currency0)}/${currencyId(currency1)}`}>
              Remove liquidity
            </ActionLink>
            <ActionLink to={`/migrate/v2/${pair.liquidityToken.address}`}>Migrate liquidity</ActionLink>
          </V2Actions>
        </ExpandedDetails>
      )}
    </V2Card>
  )
}

export default function V2Positions({
  searchQuery,
  visible,
  rowOrder,
  positionMetrics,
  onSummaryChange,
  onPositionMetricsChange,
}: {
  searchQuery: string
  visible: boolean
  rowOrder: Record<string, number>
  positionMetrics: Record<string, PositionMetrics>
  onSummaryChange: (summary: V2LiquiditySummary) => void
  onPositionMetricsChange: (positionKey: string, metrics?: PositionMetrics) => void
}) {
  const { account, chainId } = useWeb3React()
  const unsupported = Boolean(chainId && UNSUPPORTED_V2POOL_CHAIN_IDS.includes(chainId))

  let trackedTokenPairs = useTrackedTokenPairs()
  if (unsupported) trackedTokenPairs = []
  const tokenPairsWithLiquidityTokens = useMemo(
    () =>
      trackedTokenPairs.map((tokens) => ({
        liquidityToken: toV2LiquidityToken(tokens),
        tokens,
      })),
    [trackedTokenPairs]
  )
  const liquidityTokens = useMemo(
    () => tokenPairsWithLiquidityTokens.map(({ liquidityToken }) => liquidityToken),
    [tokenPairsWithLiquidityTokens]
  )
  const [v2PairBalances, fetchingBalances] = useTokenBalancesWithLoadingIndicator(account ?? undefined, liquidityTokens)
  const liquidityTokensWithBalances = useMemo(
    () =>
      tokenPairsWithLiquidityTokens.filter(({ liquidityToken }) =>
        v2PairBalances[liquidityToken.address]?.greaterThan('0')
      ),
    [tokenPairsWithLiquidityTokens, v2PairBalances]
  )
  const v2Pairs = useV2Pairs(liquidityTokensWithBalances.map(({ tokens }) => tokens))
  const walletPairs = v2Pairs.map(([, pair]) => pair).filter((pair): pair is Pair => Boolean(pair))
  const stakingInfo = useStakingInfo()
  const stakingInfosWithBalance = stakingInfo?.filter((pool) =>
    JSBI.greaterThan(pool.stakedAmount.quotient, BIG_INT_ZERO)
  )
  const stakingPairs = useV2Pairs(stakingInfosWithBalance?.map(({ tokens }) => tokens))
  const walletPairsWithoutStaked = walletPairs.filter(
    (pair) =>
      !stakingPairs.some(([, stakingPair]) => stakingPair?.liquidityToken.address === pair.liquidityToken.address)
  )
  const entries = useMemo(
    () => [
      ...walletPairsWithoutStaked.map((pair) => ({
        pair,
        stakedBalance: undefined,
        key: pair.liquidityToken.address,
      })),
      ...stakingPairs.flatMap(([, pair], index) =>
        pair
          ? [
              {
                pair,
                stakedBalance: stakingInfosWithBalance?.[index]?.stakedAmount,
                key: pair.liquidityToken.address,
              },
            ]
          : []
      ),
    ],
    [stakingInfosWithBalance, stakingPairs, walletPairsWithoutStaked]
  )
  const summary = useMemo<V2LiquiditySummary>(() => {
    const metrics = entries.map(({ key }) => positionMetrics[`v2:${key}`])
    const priced = metrics
      .map((metric) => metric?.estimatedValue)
      .filter((value): value is number => value !== undefined)
    return {
      estimatedValue: priced.length ? priced.reduce((total, value) => total + value, 0) : undefined,
      oneLocked: metrics.reduce((total, metric) => total + (metric?.oneLocked ?? 0), 0),
      positions: entries.length,
    }
  }, [entries, positionMetrics])

  useEffect(() => onSummaryChange(summary), [onSummaryChange, summary])

  const loading =
    fetchingBalances || v2Pairs.length < liquidityTokensWithBalances.length || v2Pairs.some((result) => !result)

  return (
    <>
      {unsupported
        ? visible && <EmptyState>CasinoHarmony V2 is not available on this network.</EmptyState>
        : loading
        ? visible && (
            <EmptyState>
              <Dots>
                <Trans>Loading</Trans>
              </Dots>
            </EmptyState>
          )
        : entries.map(({ pair, stakedBalance, key }) => (
            <V2PositionListItem
              key={key}
              pair={pair}
              stakedBalance={stakedBalance}
              searchQuery={searchQuery}
              visible={visible}
              order={rowOrder[`v2:${key}`]}
              onPositionMetricsChange={onPositionMetricsChange}
            />
          ))}
    </>
  )
}
